import { useState } from 'react';
import { Button, Sheet, sheetText } from '../components/ui';
import { Icon } from '../components/Icon';
import { useApp } from '../lib/app';
import type { Ride } from '../lib/types';
import { useT } from '../lib/i18n';

/** SOS: tell Zoomo Rides support (with the ride and position) and offer the one-tap call to 112. */
export function SosSheet({ open, onClose, ride }: { open: boolean; onClose: () => void; ride: Ride | null }) {
  const { api, locate, here } = useApp();
  const t = useT();
  const [sent, setSent] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  async function raise() {
    setBusy(true);
    const at = here ?? (await locate());
    const pos = at ?? ride?.driver?.location ?? null;
    try {
      await api.sos({ rideId: ride?.id, ...(pos ? { lat: pos.lat, lng: pos.lng } : {}) });
      setSent(t('sos.ok'));
    } catch {
      setSent(t('sos.failed'));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Sheet open={open} onClose={() => (setSent(null), onClose())} title={sent ? t('sos.sent') : t('sos.title')}>
      <p style={sheetText}>{sent ?? t('sos.body')}</p>
      <a
        href="tel:112"
        className="zr-btn" style={{ marginTop: 20, height: 56, borderRadius: 18, background: 'var(--danger)', color: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, fontSize: 17, fontWeight: 900, textDecoration: 'none' }}
      >
        <Icon name="phone" size={20} />
        {t('sos.call')}
      </a>
      {!sent && (
        <Button busy={busy} onClick={() => void raise()} style={{ marginTop: 10 }}>
          {t('sos.alert')}
        </Button>
      )}
      <Button variant="secondary" onClick={() => (setSent(null), onClose())} style={{ marginTop: 10 }}>
        {sent ? t('common.close') : t('common.cancel')}
      </Button>
    </Sheet>
  );
}

/** "Safety" on the driver-arriving screen: SOS, share, the PIN rule. */
export function SafetySheet({ open, onClose, onSos, onShare }: { open: boolean; onClose: () => void; onSos: () => void; onShare: () => void }) {
  const t = useT();
  const row = { height: 60, border: 0, borderRadius: 18, background: 'var(--s1)', padding: '0 16px', display: 'flex', alignItems: 'center', gap: 12, color: 'var(--ink)', fontSize: 16, fontWeight: 700, cursor: 'pointer', width: '100%', textAlign: 'left' as const };
  return (
    <Sheet open={open} onClose={onClose} title={t('safety.title')}>
      <p style={sheetText}>{t('safety.body')}</p>
      <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <button type="button" className="zr-press-soft" style={{ ...row, color: 'var(--danger)' }} onClick={onSos}>
          <Icon name="shield2" size={22} /> {t('safety.sos')}
        </button>
        <button type="button" className="zr-press-soft" style={row} onClick={onShare}>
          <Icon name="share" size={22} /> {t('safety.share')}
        </button>
      </div>
    </Sheet>
  );
}
